/**
 * The night-before email: what the customer is sent the evening before the
 * crew arrives (ADR 0313).
 *
 * Jonathon wrote the letter by hand on 2026-10-27 and asked for a button on
 * the approved checklist that writes it: the rep answers a few questions —
 * the day the work starts, when the crew and the material arrive, whether a
 * dump trailer is coming, who the project manager is and whether they stay
 * all day — and the email is written for them to look over and send, from
 * Lysa's mailbox to the customer.
 *
 * Pure, so the wording is tested and the screen only gathers facts.
 */

import { COMPANY_SIGNER_NAME } from "./contract.js";
import { bookedDay, isCrewBooked } from "./crewBooking.js";
import { centralDate } from "./payoutReminder.js";

/** Whose mailbox the letter leaves from, by default: Lysa's. */
export const NIGHT_BEFORE_SENDER_NAME = COMPANY_SIGNER_NAME;

/**
 * The person the letter leaves from by default, found among the people.
 *
 * Matched on the trimmed full name, the way the permit contact was first found
 * (Settings has no row naming her for this letter). Null when nobody matches,
 * and the screen then sends from the signed-in person's own mailbox.
 */
export function nightBeforeSender<
  P extends { id: string; full_name?: string | null },
>(people: readonly P[]): P | null {
  return (
    people.find(
      (person) => (person.full_name ?? "").trim() === NIGHT_BEFORE_SENDER_NAME,
    ) ?? null
  );
}

/** The fields of a work order the start day is read from. */
export interface NightBeforeOrder {
  status?: string | null;
  scheduled_at?: string | null;
  start_at?: string | null;
  completed_at?: string | null;
}

/**
 * The day the work starts, as the question starts out: `YYYY-MM-DD` or null.
 *
 * The crew's day is `start_at` (see `bookedDay`), never `scheduled_at`, which
 * is only when somebody pressed Schedule. Only booked orders still to be done
 * count, read as the Central-time day they fall on, and the earliest one from
 * today on is the one the customer is about to see. A day already past is not
 * offered: the letter would tell them their work began last week. Null leaves
 * the question blank for the rep.
 */
export function nightBeforeStartDefault(
  orders: readonly NightBeforeOrder[],
  today: Date = new Date(),
): string | null {
  const from = centralDate(today);
  const days = orders
    // PostgREST can hand back `2026-10-28 13:00:00+00`, which not every
    // browser's `new Date` reads; the space and the short offset are mended.
    .map((order) => ({
      ...order,
      start_at:
        order.start_at
          ?.trim()
          .replace(" ", "T")
          .replace(/([+-]\d\d)$/, "$1:00") ?? null,
    }))
    .flatMap((order) => {
      const day = bookedDay(order);
      return day !== null &&
        !order.completed_at &&
        order.status !== "completed" &&
        order.status !== "paid" &&
        isCrewBooked(order)
        ? [centralDate(new Date(day))]
        : [];
    })
    .filter((day) => day >= from)
    .sort();
  return days[0] ?? null;
}

/** What the job already knows, gathered by the screen. */
export interface NightBeforeFacts {
  /** The customer's first name. Unused by the letter today; kept for a greeting. */
  customerFirstName: string;
  /** Whoever the letter is signed by — Lysa, by default. First name. */
  senderFirstName: string;
  /** The crew's next booked day, `YYYY-MM-DD`, or null. See `nightBeforeStartDefault`. */
  scheduledStart: string | null;
  /** `company_settings.name`, or the fallback. */
  companyName: string;
}

/** The few things only the rep knows. */
export interface NightBeforeAnswers {
  /** `YYYY-MM-DD` the crew starts. Blank is "we did not say". */
  start: string;
  /** "6:00 and 7:00 AM" — as it reads after "arrive between". */
  crewArrival: string;
  /** Whether the material is being delivered in the morning or is there. */
  materials: "delivering" | "on_site";
  /** "8:00 and 9:00 AM" — as it reads after "arrive between". */
  materialDelivery: string;
  /** Whether a dump trailer is coming to the driveway. */
  dumpTrailer: boolean;
  /** The project manager, as the customer will hear the name. */
  managerName: string;
  /** Whether the project manager stays on site all day or checks in. */
  managerAllDay: "all_day" | "checking_in";
  /** Anything else the customer should know, as its own paragraph. */
  notes: string;
}

/** Who runs the site unless the rep says otherwise. */
export const DEFAULT_PROJECT_MANAGER = "Sam";

/** The windows the letter has always given. */
export const DEFAULT_CREW_ARRIVAL = "6:00 and 7:00 AM";
export const DEFAULT_MATERIAL_DELIVERY = "8:00 and 9:00 AM";

/**
 * What the questions start out saying.
 *
 * The start is the booked work order's day; the windows and the project
 * manager are the ones the letter has always named, and a dump trailer is
 * assumed because a roof always has one.
 */
export function nightBeforeDefaults(
  facts: NightBeforeFacts,
): NightBeforeAnswers {
  return {
    start: facts.scheduledStart ?? "",
    crewArrival: DEFAULT_CREW_ARRIVAL,
    materials: "delivering",
    materialDelivery: DEFAULT_MATERIAL_DELIVERY,
    dumpTrailer: true,
    managerName: DEFAULT_PROJECT_MANAGER,
    managerAllDay: "checking_in",
    notes: "",
  };
}

/** A `YYYY-MM-DD` as a local date, or null for anything else. */
function localDay(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return Number.isNaN(date.getTime()) ? null : date;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * When the work starts, as the letter says it: "tomorrow" when it is, "today"
 * when the letter is late, else "on Friday, October 30". Blank when the rep
 * left the day blank, so the sentence reads without one.
 */
export function nightBeforeStart(start: string, today: Date): string {
  const day = localDay(start);
  if (!day) return "";
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (sameDay(day, tomorrow)) return "tomorrow";
  if (sameDay(day, today)) return "today";
  return `on ${day.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  })}`;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? "";
}

/** "between 6:00 and 7:00 AM", or nothing when the rep blanked the window. */
function between(window: string): string {
  const text = window.trim();
  return text ? ` between ${text}` : "";
}

/** What the driveway has to be clear for. */
function drivewayFor(answers: NightBeforeAnswers): string {
  const delivering = answers.materials === "delivering";
  if (answers.dumpTrailer && delivering)
    return "our dump trailer and material delivery";
  if (answers.dumpTrailer) return "our dump trailer";
  if (delivering) return "the material delivery";
  return "our crew";
}

function paragraphs(text: string): string[] {
  return text
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
}

/** The subject and body of the night-before email. */
export function nightBeforeEmail(
  facts: NightBeforeFacts,
  answers: NightBeforeAnswers,
  today: Date = new Date(),
): { subject: string; body: string } {
  const when = nightBeforeStart(answers.start, today);
  const manager = answers.managerName.trim() || DEFAULT_PROJECT_MANAGER;
  // "contact either Sam or me" is silly when Sam is the one writing.
  const managerIsSender =
    firstName(manager).toLowerCase() ===
    firstName(facts.senderFirstName).toLowerCase();
  const morning = [
    "deliver additional materials",
    ...(answers.dumpTrailer ? ["position the dump trailer"] : []),
    "initiate the crew's work",
  ];
  const morningList =
    morning.length === 3
      ? `${morning[0]}, ${morning[1]}, and ${morning[2]}`
      : `${morning[0]} and ${morning[1]}`;
  const rest =
    answers.managerAllDay === "all_day"
      ? `${manager} will be on site throughout the day to oversee the project's progress.`
      : `${manager} will not be on site all day but will maintain constant communication with the installation crew to oversee the project's progress.`;

  const body = [
    "Good evening!",
    `We're thrilled to commence your roofing project${when ? ` ${when}` : ""}! I've compiled a few reminders to ensure a smooth and efficient process.`,
    `Crew Arrival: Our installation crew is scheduled to arrive${between(answers.crewArrival)}.`,
    `Driveway Access: Please ensure that all vehicles are relocated to the front of the house, and the driveway remains clear to accommodate ${drivewayFor(answers)}.`,
    answers.materials === "on_site"
      ? "Material Delivery: Your roofing materials are already on site."
      : `Material Delivery: Your roofing materials are expected to arrive${between(answers.materialDelivery)}.`,
    `Project Manager: ${manager}, our project manager, will be on-site in the morning to ${morningList}. ${rest}`,
    ...paragraphs(answers.notes),
    `If you have any questions or concerns before or during the installation, please don't hesitate to contact ${managerIsSender ? "me" : `either ${manager} or me`}. We're here to assist you at all times!`,
    `Thank you once again for entrusting ${facts.companyName} with your home. We eagerly anticipate the commencement of your project!`,
  ];

  return {
    subject: when
      ? `Your roofing project starts ${when}`
      : "Your roofing project is about to start",
    body: body.join("\n\n"),
  };
}
